import { PathwayLight } from '@/types';
import { Link } from 'react-router-dom';
import { useState, useCallback } from 'react';
import {
  Card,
  CardButton,
  CardMenu,
  CardMenuItem,
  Content,
  Heading,
  Inline,
  InlineItem,
  Secondary,
  ConfirmationDialog,
} from '@kinesis/bungle';
import { PathwaysIcon } from '@/components/pathways-icon';
import { pathwayPath } from '@/links';
import { formatDistanceStrings } from '@/date';
import { noop } from 'lodash/fp';

// One pathway row: Card with a link-button to the detail page, delete via
// the card menu — mirrors `scenarios-list/item`.
const Item = (props: ItemProps) => {
  const { pathway, now, onDelete = noop } = props;
  const [deletePrompt, setDeletePrompt] = useState(false);
  const onDeletePrompt = useCallback(() => {
    setDeletePrompt(true);
  }, [setDeletePrompt]);
  const closeDeletePrompt = useCallback(() => {
    setDeletePrompt(false);
  }, [setDeletePrompt]);

  const onDeleteConfirm = useCallback(() => {
    onDelete(pathway.id);
  }, [pathway, onDelete]);

  return (
    <>
      <Content borderRadiusSize='small'>
        <Card>
          <CardButton as={Link} to={pathwayPath(pathway.inventory, pathway.id)}>
            <Content padding='medium' paddingMode='equal'>
              <Inline space='medium'>
                <InlineItem>
                  <PathwaysIcon />
                </InlineItem>
                <InlineItem ellipsify>
                  <Heading as='h3' size='medium'>
                    {pathway.name}
                  </Heading>
                  <Secondary>{`Last updated ${formatDistanceStrings(now, pathway.updatedAt)}`}</Secondary>
                </InlineItem>
              </Inline>
            </Content>
          </CardButton>
          <CardMenu>
            <CardMenuItem variant='danger' onClick={onDeletePrompt}>
              Delete
            </CardMenuItem>
          </CardMenu>
        </Card>
      </Content>
      {deletePrompt && (
        <ConfirmationDialog
          cancelText='Don’t delete'
          confirmText='Delete pathway'
          onCancel={closeDeletePrompt}
          onConfirm={onDeleteConfirm}
          title={`Delete pathway?`}
          variant='danger'
        >
          This pathway and its interventions will be removed and can not be
          recovered.
        </ConfirmationDialog>
      )}
    </>
  );
};

export type ItemProps = {
  pathway: PathwayLight;
  onDelete?: (pathway: number) => void;
  now: string;
};

export { Item };
